import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Download, RefreshCw, Sparkles } from 'lucide-react';
import { Radar, RadarChart, PolarAngleAxis, PolarGrid, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import QRCode from 'qrcode';
import XuanYuanReportFooter from './XuanYuanReportFooter';
import XuanYuanTopBar from './XuanYuanTopBar';

interface MbtiStageTestProps {
  onBack: () => void;
  onBeforeShowResult?: (testId: string) => Promise<boolean>;
}

type ScoreRecord = Record<string, { total: number; count: number }>;
type MbtiStageHistoryRecord = {
  id: string;
  createdAt: number;
  scores: ScoreRecord;
};
type StageOption = { text: string; score: Record<string, number> };
type StageQuestion = { text: string; options: [StageOption, StageOption] };

const MBTI_STAGE_HISTORY_KEY = 'mbti_stage_history_v1';
const MAX_MBTI_STAGE_HISTORY = 8;
const SITE_URL = 'https://qimen.aifashou.cn';

const q = (
  text: string,
  optionA: string,
  scoreA: Record<string, number>,
  optionB: string,
  scoreB: Record<string, number>,
): StageQuestion => ({
  text,
  options: [
    { text: optionA, score: scoreA },
    { text: optionB, score: scoreB },
  ],
});

const QUESTIONS: StageQuestion[] = [
  q('1. 刚进入一个新团队时，你通常会：', '主动发起交流，快速熟络氛围', { E: 1, social: 5, self_accept: 3 }, '先观察一段时间，再选择性互动', { I: 1, social: 3, self_accept: 5 }),
  q('2. 周末你更愿意：', '约朋友一起出门，现场决定行程', { E: 1, social: 5, vision: 3 }, '独处做喜欢的事，节奏由自己掌控', { I: 1, social: 2, self_accept: 5 }),
  q('3. 讨论中有新想法时，你会：', '边想边说，先碰撞再打磨', { E: 1, social: 4, decision: 3 }, '先在脑中整理，再精准表达', { I: 1, decision: 5, self_accept: 4 }),
  q('4. 面对意见冲突时，你更常：', '当场沟通，快速把分歧摊开', { E: 1, social: 5, emotion: 3 }, '先沉淀情绪，再一对一沟通', { I: 1, emotion: 5, self_accept: 4 }),
  q('5. 高压一周后，你恢复状态的方式是：', '找人聊天或活动，靠外界充电', { E: 1, social: 5, emotion: 3 }, '独处放空，靠内在回稳', { I: 1, self_accept: 5, emotion: 4 }),
  q('6. 在会议里你更接近：', '先抛观点，边讨论边修正', { E: 1, social: 4, decision: 3 }, '先听全局，再给出结论', { I: 1, decision: 5, self_accept: 4 }),
  q('7. 遇到陌生场景时，你会：', '快速接触人群，边做边熟悉', { E: 1, social: 5, vision: 3 }, '先建立安全感，再逐步进入状态', { I: 1, self_accept: 5, social: 3 }),
  q('8. 当你需要影响他人时，你更倾向：', '通过现场感染力推动执行', { E: 1, social: 5, emotion: 3 }, '通过深度沟通建立信任后推进', { I: 1, emotion: 5, self_accept: 4 }),

  q('9. 制定方案时你更相信：', '已有经验与可验证事实', { S: 1, decision: 5, vision: 3 }, '趋势与潜在机会的判断', { N: 1, vision: 5, decision: 3 }),
  q('10. 你更容易被哪类内容打动：', '具体案例、流程与落地细节', { S: 1, decision: 5, vision: 2 }, '概念框架、未来图景与可能性', { N: 1, vision: 5, decision: 3 }),
  q('11. 接手新任务时你会先：', '确认资源、边界和执行条件', { S: 1, decision: 5, self_accept: 3 }, '先构建目标愿景和价值方向', { N: 1, vision: 5, self_accept: 4 }),
  q('12. 面对不确定性时你更常：', '收集数据，先稳住基本盘', { S: 1, decision: 5, emotion: 3 }, '先建立假设，再快速试错', { N: 1, vision: 5, decision: 3 }),
  q('13. 在工作协作里，你更看重：', '规则清晰、步骤可控', { S: 1, decision: 5, social: 3 }, '方向一致、创意空间', { N: 1, vision: 5, social: 4 }),
  q('14. 你更擅长的表达方式是：', '把复杂事讲得具体可做', { S: 1, decision: 5, social: 4 }, '把碎片信息连接成新洞察', { N: 1, vision: 5, social: 4 }),
  q('15. 你做判断的第一反应常是：', '这件事现实上可不可行', { S: 1, decision: 5, vision: 2 }, '这件事长期上值不值得', { N: 1, vision: 5, decision: 3 }),
  q('16. 当你规划未来时，你更像：', '稳扎稳打的路径设计者', { S: 1, decision: 5, self_accept: 3 }, '跨维度联想的机会整合者', { N: 1, vision: 5, self_accept: 4 }),

  q('17. 做关键决策时你优先：', '标准、逻辑与一致性', { T: 1, decision: 5, emotion: 2 }, '关系感受与长期合作感', { F: 1, emotion: 5, decision: 3 }),
  q('18. 对你来说高质量反馈更像：', '直接指出问题和改进点', { T: 1, decision: 5, social: 3 }, '兼顾感受并给出鼓励路径', { F: 1, emotion: 5, social: 4 }),
  q('19. 当他人犯错时，你更常：', '先复盘机制与责任边界', { T: 1, decision: 5, emotion: 2 }, '先理解动机与压力背景', { F: 1, emotion: 5, self_accept: 4 }),
  q('20. 你在团队里天然扮演：', '结构化推进者', { T: 1, decision: 5, social: 3 }, '氛围稳定器与共识协调者', { F: 1, emotion: 5, social: 5 }),
  q('21. 遇到复杂分歧时你更先：', '拆解事实、排优先级', { T: 1, decision: 5, vision: 3 }, '对齐情绪、重建信任', { F: 1, emotion: 5, social: 5 }),
  q('22. 你更认可的领导方式是：', '目标清晰、奖惩明确', { T: 1, decision: 5, social: 3 }, '有温度、有连接、有成长感', { F: 1, emotion: 5, self_accept: 4 }),
  q('23. 当自己状态不佳时，你更会：', '先保证结果，再处理情绪', { T: 1, decision: 5, emotion: 2 }, '先安顿情绪，再恢复执行', { F: 1, emotion: 5, self_accept: 5 }),
  q('24. 在亲密关系里你更重视：', '原则与边界清晰', { T: 1, decision: 5, self_accept: 3 }, '理解与情感回应', { F: 1, emotion: 5, social: 4 }),

  q('25. 你做项目更偏好：', '先定计划，再按节点推进', { J: 1, decision: 5, self_accept: 4 }, '先跑起来，再持续迭代', { P: 1, vision: 5, decision: 3 }),
  q('26. 对待截止日期你通常：', '提前分解任务，留缓冲', { J: 1, decision: 5, emotion: 4 }, '临近节点集中爆发', { P: 1, vision: 4, emotion: 3 }),
  q('27. 出行或活动你更习惯：', '提前安排路线与节奏', { J: 1, decision: 5, self_accept: 4 }, '到现场根据状态调整', { P: 1, vision: 5, self_accept: 3 }),
  q('28. 面对机会窗口时你会：', '先评估风险再决定是否出手', { J: 1, decision: 5, vision: 3 }, '先抓住机会，再优化细节', { P: 1, vision: 5, decision: 3 }),
  q('29. 你工作台与文件管理通常：', '分类清楚，随时可检索', { J: 1, decision: 5, social: 3 }, '灵感优先，动态整理', { P: 1, vision: 5, social: 3 }),
  q('30. 当计划被打乱时你更容易：', '快速重排优先级，恢复秩序', { J: 1, decision: 5, emotion: 4 }, '顺势重构方案，寻找新解', { P: 1, vision: 5, emotion: 3 }),
  q('31. 你给自己的成长策略更像：', '长期主义、稳定复利', { J: 1, decision: 5, self_accept: 4 }, '多线程探索、阶段跃迁', { P: 1, vision: 5, self_accept: 4 }),
  q('32. 对你而言理想状态是：', '可控节奏中持续兑现目标', { J: 1, decision: 5, emotion: 4 }, '开放空间中不断创造可能', { P: 1, vision: 5, emotion: 3 }),
];

const ROLE_MAP: Record<string, string> = {
  ISTJ: '物流师', ISFJ: '守卫者', INFJ: '提倡者', INTJ: '建筑师',
  ISTP: '鉴赏家', ISFP: '探险家', INFP: '调停者', INTP: '逻辑学家',
  ESTP: '企业家', ESFP: '表演者', ENFP: '竞选者', ENTP: '辩论家',
  ESTJ: '总经理', ESFJ: '执政官', ENFJ: '主人公', ENTJ: '指挥官',
};

const LOOP_MAP: Record<string, string> = {
  INFP: 'Fi-Si Loop（情绪回圈模式）',
  INFJ: 'Ni-Ti Loop（理想内卷模式）',
  INTP: 'Ti-Si Loop（过度求稳模式）',
  INTJ: 'Ni-Fi Loop（主观封闭模式）',
  ENFP: 'Ne-Te Loop（高压冲刺模式）',
  ENFJ: 'Fe-Se Loop（外界过载模式）',
  ENTP: 'Ne-Fe Loop（社交分心模式）',
  ENTJ: 'Te-Se Loop（执行过驱模式）',
  ISFP: 'Fi-Ni Loop（自我怀疑模式）',
  ISFJ: 'Si-Ti Loop（谨慎过度模式）',
  ISTP: 'Ti-Ni Loop（独行封闭模式）',
  ISTJ: 'Si-Fi Loop（压力内耗模式）',
  ESFP: 'Se-Te Loop（冲动决策模式）',
  ESFJ: 'Fe-Ne Loop（关系超负荷模式）',
  ESTP: 'Se-Fe Loop（刺激依赖模式）',
  ESTJ: 'Te-Ne Loop（控制焦虑模式）',
};

const METRIC_LABELS: Array<{ key: string; label: string }> = [
  { key: 'decision', label: '理性决策' },
  { key: 'self_accept', label: '自我接纳' },
  { key: 'emotion', label: '情绪稳定' },
  { key: 'social', label: '社会适应' },
  { key: 'vision', label: '人生格局' },
];

const opposite = (char: string): string => {
  const map: Record<string, string> = { E: 'I', I: 'E', S: 'N', N: 'S', T: 'F', F: 'T', J: 'P', P: 'J' };
  return map[char] || char;
};

const mergeScoresInto = (base: ScoreRecord, scoreObj: Record<string, number>, add: boolean): ScoreRecord => {
  const next = { ...base };
  for (const [key, val] of Object.entries(scoreObj)) {
    if (!next[key]) next[key] = { total: 0, count: 0 };
    next[key] = {
      total: next[key]!.total + (add ? val : -val),
      count: next[key]!.count + (add ? 1 : -1),
    };
  }
  return next;
};

const getAvg = (scores: ScoreRecord, key: string): number => {
  if (!scores[key] || scores[key].count === 0) return 0;
  return scores[key].total / scores[key].count;
};

const getPct = (scores: ScoreRecord, key: string): number => {
  const avg = getAvg(scores, key);
  const raw = ((avg - 1) / 4) * 100;
  return Math.max(0, Math.min(100, Math.round(raw)));
};

const clamp = (n: number, min: number, max: number): number => Math.max(min, Math.min(max, n));

const getTalentTags = (type: string): string[] => {
  const tags: string[] = [];
  tags.push(type.includes('N') ? '内容创作' : '项目运营');
  tags.push(type.includes('T') ? '战略分析' : '教育咨询');
  tags.push(type.startsWith('E') ? '商业拓展' : '研究策划');
  tags.push(type.endsWith('J') ? '组织管理' : '产品创新');
  return Array.from(new Set(tags)).slice(0, 4);
};

const getStrengthText = (type: string, topMetricLabel: string): string =>
  `你的人格主轴是 ${type}（${ROLE_MAP[type] || '复合型'}），当前最突出的优势是「${topMetricLabel}」。这意味着你不仅有稳定的个体风格，也具备把优势迁移到学习、关系与职业场景中的能力。`;

const getBlindText = (lowMetricLabel: string): string => {
  const map: Record<string, string> = {
    理性决策: '你可能在关键节点因为信息噪音而犹豫，建议建立“事实-判断-行动”的三段式决策模板。',
    自我接纳: '你偶尔会对自己过于苛刻，建议增加“完成复盘”而非“结果责备”，让成长可持续。',
    情绪稳定: '你对外界刺激较敏感，建议保留固定的情绪回稳动作（呼吸、散步、记录），降低内耗。',
    社会适应: '你在高密度社交中可能会透支，建议把“深连接”优先于“广连接”，提升社交质量。',
    人生格局: '你容易陷在当下执行里，建议每周留出一次“远景时间”，校准长期方向。',
  };
  return map[lowMetricLabel] || '保持节奏稳定，持续做小步复盘，你会看到明显提升。';
};

const getActions = (lowMetricLabel: string): string[] => {
  const map: Record<string, string[]> = {
    理性决策: ['把重大选择写成 3 个选项 + 各自最坏结果。', '每次决策限定 24 小时内给出可执行版本。', '在执行后做 10 分钟复盘，迭代自己的判断模型。'],
    自我接纳: ['每天记录 1 条“今天做得好的事”。', '把“必须完美”替换为“先完成再优化”。', '每周给自己一次无评价的休整时段。'],
    情绪稳定: ['建立个人“情绪预警清单”，提前识别触发点。', '出现波动时先做 90 秒深呼吸，再做回应。', '把高压任务切分为 25 分钟冲刺单元。'],
    社会适应: ['优先维护 3 个高质量关系，而不是泛社交。', '重要沟通前先写下目标与边界。', '在冲突后用“事实-感受-请求”模板复盘。'],
    人生格局: ['每周做一次 30 分钟长期目标对齐。', '把今年目标拆成季度里程碑并上墙。', '每月主动接触一个跨领域新输入。'],
  };
  return map[lowMetricLabel] || ['保持记录', '保持复盘', '保持行动'];
};

const getStageDesc = (stage: string): string => {
  if (stage === '高阶') return '你已形成稳定人格优势与外化能力，能把天赋转化为长期价值。';
  if (stage === '中阶') return '你正在完成从“自我理解”到“稳定输出”的关键跃迁。';
  if (stage === '进阶') return '你已具备明确潜能，下一步重点是提升稳定性与持续性。';
  return '你处在人格能力的起步阶段，先建立方法，再逐步放大优势。';
};

const formatHistoryTime = (ts: number): string => {
  if (!Number.isFinite(ts) || ts <= 0) return '--';
  return new Date(ts).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const loadMbtiStageHistory = (): MbtiStageHistoryRecord[] => {
  try {
    const raw = localStorage.getItem(MBTI_STAGE_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item === 'object' && item.scores)
      .map((item) => ({
        id: String(item.id || ''),
        createdAt: Number(item.createdAt || 0),
        scores: item.scores as ScoreRecord,
      }))
      .filter((item) => item.id);
  } catch {
    return [];
  }
};

export default function MbtiStageTest({ onBack, onBeforeShowResult }: MbtiStageTestProps) {
  const [step, setStep] = useState<'intro' | 'quiz' | 'result'>('intro');
  const [answeredScores, setAnsweredScores] = useState<Array<Record<string, number>>>([]);
  const [scores, setScores] = useState<ScoreRecord>({});
  const [historyRecords, setHistoryRecords] = useState<MbtiStageHistoryRecord[]>([]);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [lockedNotice, setLockedNotice] = useState('');
  const reportRef = useRef<HTMLDivElement | null>(null);

  const qIndex = answeredScores.length;
  const currentQuestion = QUESTIONS[qIndex];
  const progress = ((qIndex + 1) / QUESTIONS.length) * 100;

  useEffect(() => {
    setHistoryRecords(loadMbtiStageHistory());
  }, []);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(SITE_URL, { width: 220, margin: 1 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openResultWithGuard = async (): Promise<void> => {
    const canShow = onBeforeShowResult ? await onBeforeShowResult('mbti_stage') : true;
    if (!canShow) {
      setLockedNotice('你已使用过免费心理测试次数，当前报告需激活码解锁后查看。');
      setStep('intro');
      return;
    }
    setLockedNotice('');
    setStep('result');
  };

  const saveHistoryRecord = (nextScores: ScoreRecord) => {
    const record: MbtiStageHistoryRecord = {
      id: `mbti_stage_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      createdAt: Date.now(),
      scores: nextScores,
    };
    setHistoryRecords((prev) => {
      const merged = [record, ...prev].slice(0, MAX_MBTI_STAGE_HISTORY);
      localStorage.setItem(MBTI_STAGE_HISTORY_KEY, JSON.stringify(merged));
      return merged;
    });
  };

  const openHistoryRecord = (record: MbtiStageHistoryRecord) => {
    setScores(record.scores);
    setAnsweredScores([]);
    void openResultWithGuard();
  };

  const handleStart = () => {
    setAnsweredScores([]);
    setScores({});
    setStep('quiz');
  };

  const handleViewSample = () => {
    const sampleScores = QUESTIONS
      .map((item) => item.options[Math.floor(Math.random() * item.options.length)].score)
      .reduce((acc, item) => mergeScoresInto(acc, item, true), {} as ScoreRecord);
    setAnsweredScores([]);
    setScores(sampleScores);
    void openResultWithGuard();
  };

  const handleAnswer = (scoreObj: Record<string, number>) => {
    const next = [...answeredScores, scoreObj];
    setAnsweredScores(next);
    const nextScores = next.reduce((acc, item) => mergeScoresInto(acc, item, true), {} as ScoreRecord);
    setScores(nextScores);
    if (next.length >= QUESTIONS.length) {
      saveHistoryRecord(nextScores);
      void openResultWithGuard();
    }
  };

  const handlePrev = () => {
    if (answeredScores.length === 0) return;
    const last = answeredScores[answeredScores.length - 1]!;
    const next = answeredScores.slice(0, -1);
    setAnsweredScores(next);
    setScores((prev) => mergeScoresInto(prev, last, false));
  };

  const handleReset = () => {
    setStep('intro');
    setAnsweredScores([]);
    setScores({});
    setLockedNotice('');
  };

  const report = useMemo(() => {
    const getSum = (k: string) => (scores[k] ? scores[k].total : 0);
    const E = getSum('E'); const I = getSum('I');
    const S = getSum('S'); const N = getSum('N');
    const T = getSum('T'); const F = getSum('F');
    const J = getSum('J'); const P = getSum('P');
    const type = `${E >= I ? 'E' : 'I'}${S >= N ? 'S' : 'N'}${T >= F ? 'T' : 'F'}${J >= P ? 'J' : 'P'}`;

    const metricRows = METRIC_LABELS.map((item) => ({
      ...item,
      value: getPct(scores, item.key),
    }));
    const metricMap = metricRows.reduce<Record<string, number>>((acc, item) => {
      acc[item.label] = item.value;
      return acc;
    }, {});
    const avg = metricRows.reduce((sum, item) => sum + item.value, 0) / metricRows.length;
    const totalScore = Math.round(avg * 1.6);
    const stage = totalScore >= 136 ? '高阶' : totalScore >= 104 ? '中阶' : totalScore >= 72 ? '进阶' : '初阶';
    const topMetric = [...metricRows].sort((a, b) => b.value - a.value)[0];
    const lowMetric = [...metricRows].sort((a, b) => a.value - b.value)[0];

    const matchA = `${opposite(type[0] || 'I')}${type[1] || 'N'}${type[2] || 'F'}${opposite(type[3] || 'P')}`;
    const matchB = `${opposite(type[0] || 'I')}${type[1] || 'N'}${opposite(type[2] || 'F')}${opposite(type[3] || 'P')}`;
    const matchAScore = clamp(Math.round((metricMap['情绪稳定'] + metricMap['社会适应'] + metricMap['自我接纳']) / 3 + 8), 76, 99);
    const matchBScore = clamp(matchAScore - 7, 68, 96);

    return {
      type,
      role: ROLE_MAP[type] || '复合型人格',
      stage,
      totalScore,
      metricRows,
      topMetricLabel: topMetric?.label || '理性决策',
      lowMetricLabel: lowMetric?.label || '社会适应',
      talentTags: getTalentTags(type),
      matchA,
      matchB,
      matchAScore,
      matchBScore,
      loop: LOOP_MAP[type] || `${type} Loop（阶段整合模式）`,
    };
  }, [scores]);

  const downloadImage = () => {
    const html2canvasFn = (window as unknown as { html2canvas?: (el: HTMLElement, opt?: unknown) => Promise<HTMLCanvasElement> }).html2canvas;
    if (!html2canvasFn || !reportRef.current) {
      alert('截图引擎正在加载，请稍后重试。');
      return;
    }
    html2canvasFn(reportRef.current, { scale: 2, useCORS: true, backgroundColor: '#f0fbf6' }).then((canvas) => {
      const link = document.createElement('a');
      link.download = `MBTI阶段_${report.type}_报告.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    });
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(120%_95%_at_50%_-12%,#f2dfbd_0%,#eee3d0_36%,#eceff5_72%,#eef2f8_100%)] text-slate-800 font-sans flex flex-col">
      <XuanYuanTopBar
        title="轩辕国学·MBTI阶段"
        onBack={onBack}
        variant="lightBlend"
      />
      {lockedNotice && (
        <div className="mx-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {lockedNotice}
        </div>
      )}

      {step === 'intro' && (
        <div className="flex-1 overflow-y-auto bg-transparent p-6">
          <div className="flex-1 flex flex-col justify-center items-center text-center max-w-2xl mx-auto w-full">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-inner bg-emerald-50">
              <Sparkles size={30} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-black text-gray-900 mb-3">MBTI阶段测试</h2>
            <p className="text-gray-500 text-xs leading-relaxed mb-6 px-4">
              32题测出你的 MBTI 人格类型与心智阶段，输出雷达图、进化行动与匹配建议。
            </p>
            <div className="flex space-x-8 rounded-2xl border border-white/75 bg-white/60 px-5 py-3 mb-8 w-full justify-center backdrop-blur-sm">
              <div className="flex flex-col items-center">
                <span className="text-gray-400 text-xs mb-1">精选题库</span>
                <span className="font-bold text-gray-800 text-base">32 题</span>
              </div>
              <div className="w-px bg-gray-200"></div>
              <div className="flex flex-col items-center">
                <span className="text-gray-400 text-xs mb-1">预计耗时</span>
                <span className="font-bold text-gray-800 text-base">6-8分钟</span>
              </div>
            </div>
            <button
              onClick={handleStart}
              className="w-full py-3 rounded-full bg-blue-600 text-white font-bold text-base shadow-lg hover:bg-blue-700"
            >
              开始测试
            </button>
            <button
              onClick={handleViewSample}
              className="w-full mt-4 py-2.5 rounded-full bg-gray-100 text-gray-600 font-bold text-[13px] hover:bg-gray-200"
            >
              查看报告样例
            </button>
            {historyRecords.length > 0 && (
              <div className="w-full mt-6 rounded-2xl border border-white/75 bg-white/60 p-4 text-left backdrop-blur-sm">
                <div className="text-sm font-bold text-gray-800 mb-3">历史报告</div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {historyRecords.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => openHistoryRecord(item)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-left hover:border-blue-300 hover:bg-blue-50/40"
                    >
                      <div className="text-xs text-slate-500">{formatHistoryTime(item.createdAt)}</div>
                      <div className="text-sm font-semibold text-slate-800 mt-0.5">查看当次 MBTI 阶段报告</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === 'quiz' && currentQuestion && (
        <div className="flex-1 flex flex-col">
          <div className="w-full h-1 bg-emerald-100">
            <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex-1 p-5 max-w-2xl w-full mx-auto overflow-y-auto">
            <div className="text-xs font-bold text-emerald-600 tracking-widest uppercase mb-3">进度 {qIndex + 1} / {QUESTIONS.length}</div>
            <h2 className="text-xl font-bold text-slate-900 leading-relaxed mb-8">{currentQuestion.text}</h2>
            <div className="space-y-3">
              {currentQuestion.options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={(event) => {
                    event.currentTarget.blur();
                    handleAnswer(opt.score);
                  }}
                  className="w-full text-left rounded-xl border border-emerald-100 bg-white p-4 font-medium text-slate-700 active:border-emerald-300 active:bg-emerald-50/30 md:hover:border-emerald-400 md:hover:bg-emerald-50/40 transition-colors"
                >
                  {opt.text}
                </button>
              ))}
            </div>
            {qIndex > 0 && (
              <button
                onClick={handlePrev}
                className="w-full mt-4 py-3 rounded-xl bg-slate-100 border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-200"
              >
                上一题
              </button>
            )}
          </div>
        </div>
      )}

      {step === 'result' && (
        <>
          <div className="flex-1 overflow-y-auto p-4 pb-24">
            <div ref={reportRef} className="max-w-2xl mx-auto space-y-4">
              <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-5 shadow-sm">
                <div className="text-xs opacity-90 tracking-wider">16人格 · 心智阶段</div>
                <div className="mt-2 flex items-end justify-between">
                  <div>
                    <div className="text-4xl font-black">{report.stage}</div>
                    <div className="text-3xl font-black mt-1">{report.type}</div>
                    <div className="text-sm opacity-90 mt-1">{report.role}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs opacity-85">总分</div>
                    <div className="text-2xl font-black">{report.totalScore}</div>
                  </div>
                </div>
                <p className="text-sm leading-7 mt-3 text-emerald-50">{getStageDesc(report.stage)}</p>
              </div>

              <div className="bg-white rounded-2xl border border-emerald-100 p-5 shadow-sm">
                <h3 className="font-bold text-slate-900 mb-3">阶段能量图谱</h3>
                <div className="h-52 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="68%" data={report.metricRows.map((item) => ({ subject: item.label, value: item.value }))}>
                      <PolarGrid stroke="#d1fae5" />
                      <PolarAngleAxis dataKey="subject" tick={{ fill: '#065f46', fontSize: 11 }} />
                      <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                      <Radar dataKey="value" stroke="#059669" strokeWidth={2} fill="#34d399" fillOpacity={0.25} dot={{ r: 3, fill: '#047857' }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 mt-4">
                  {report.metricRows.map((item) => (
                    <div key={item.key} className="flex items-center text-sm">
                      <div className="w-20 text-slate-700 font-semibold text-xs">{item.label}</div>
                      <div className="flex-1 h-2.5 rounded-full bg-emerald-50 mx-2 overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${item.value}%` }} />
                      </div>
                      <div className="w-10 text-right text-emerald-700 font-black text-sm">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-emerald-100 p-5 shadow-sm">
                <h3 className="font-bold text-slate-900">人生罗盘</h3>
                <div className="mt-3">
                  <div className="text-xs text-slate-500 mb-2">天赋职业方向</div>
                  <div className="flex flex-wrap gap-2">
                    {report.talentTags.map((tag) => (
                      <span key={tag} className="text-xs px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 font-semibold">{tag}</span>
                    ))}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-slate-500 mb-2">灵魂伴侣契合度</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-slate-700">{report.matchA}</div>
                      <div className="text-rose-500 font-black">{report.matchAScore}%</div>
                    </div>
                    <div className="h-2 rounded-full bg-rose-50 overflow-hidden">
                      <div className="h-full bg-rose-400 rounded-full" style={{ width: `${report.matchAScore}%` }} />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-slate-700">{report.matchB}</div>
                      <div className="text-rose-500 font-black">{report.matchBScore}%</div>
                    </div>
                    <div className="h-2 rounded-full bg-rose-50 overflow-hidden">
                      <div className="h-full bg-rose-300 rounded-full" style={{ width: `${report.matchBScore}%` }} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-emerald-100 p-5 shadow-sm">
                <h3 className="font-bold text-slate-900 mb-3">人格深度说明书</h3>
                <div className="space-y-3 text-sm leading-7">
                  <div>
                    <div className="font-semibold text-emerald-700">天赋原力</div>
                    <div className="text-slate-600">{getStrengthText(report.type, report.topMetricLabel)}</div>
                  </div>
                  <div>
                    <div className="font-semibold text-emerald-700">潜在盲区</div>
                    <div className="text-slate-600">{getBlindText(report.lowMetricLabel)}</div>
                  </div>
                  <div>
                    <div className="font-semibold text-emerald-700">进化行动清单</div>
                    <div className="mt-1 space-y-1 text-slate-600">
                      {getActions(report.lowMetricLabel).map((item, idx) => (
                        <div key={item} className="flex items-start gap-2">
                          <span className="mt-0.5 inline-flex w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs items-center justify-center font-bold">{idx + 1}</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-emerald-100 p-5 shadow-sm">
                <h3 className="font-bold text-slate-900 mb-3">当前能量状态诊断</h3>
                <div className="space-y-3 text-sm leading-7 text-slate-600">
                  <div>
                    <span className="font-semibold text-emerald-700">当前阶段：</span>
                    {report.stage}。你正处于 {report.stage === '高阶' ? '稳定输出并可外溢影响他人的阶段。' : report.stage === '中阶' ? '关键跃迁窗口，适合把优势固化为方法。' : '夯实基础与建立节奏的核心阶段。'}
                  </div>
                  <div>
                    <span className="font-semibold text-emerald-700">能量整合状态：</span>
                    {report.loop}
                  </div>
                  <div>
                    <span className="font-semibold text-emerald-700">进阶处方：</span>
                    先稳定最短板维度（{report.lowMetricLabel}），再放大最强维度（{report.topMetricLabel}），你的人格势能会在 4-8 周内显著提升。
                  </div>
                </div>
              </div>

              <XuanYuanReportFooter
                qrDataUrl={qrDataUrl}
                textClassName="text-emerald-700"
                emphasizeClassName="text-emerald-800"
                qrBorderClassName="border-emerald-200"
              />

              <div className="text-xs text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" />
                报告已生成，可直接保存图片分享。
              </div>
            </div>
          </div>

          <div className="fixed bottom-6 left-4 right-4 max-w-2xl mx-auto flex gap-3">
            <button
              onClick={handleReset}
              className="flex-1 py-3 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              重新测试
            </button>
            <button
              onClick={downloadImage}
              className="flex-1 py-3 rounded-xl bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              保存报告
            </button>
          </div>
        </>
      )}
    </div>
  );
}
